import React, { useMemo, useState } from 'react';
import { Users, Download, Calendar, RefreshCw } from 'lucide-react';
import { StatusFilters } from './StatusUI';
import ContactRow from './ContactRow';

const API = process.env.REACT_APP_BACKEND_URL;

const ContactsTab = ({ contacts, loading, onRefresh, onDelete, formatDate, onStatusChange }) => {
  const [filter, setFilter] = useState('all');

  const counts = useMemo(() => ({
    all: contacts.length,
    nouveau: contacts.filter((c) => (c.status || 'nouveau') === 'nouveau').length,
    lu: contacts.filter((c) => c.status === 'lu').length,
    traite: contacts.filter((c) => c.status === 'traite').length,
  }), [contacts]);

  const filtered = useMemo(() => (
    filter === 'all' ? contacts : contacts.filter((c) => (c.status || 'nouveau') === filter)
  ), [contacts, filter]);

  const changeStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/catalogue-requests/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      onStatusChange && onStatusChange();
    } catch (_) { /* silent */ }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm" data-testid="refresh-contacts">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center"><Users className="w-6 h-6 text-orange-500" /></div>
            <div><div className="text-2xl font-bold text-gray-900">{counts.all}</div><div className="text-sm text-gray-500">Total contacts</div></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center"><Download className="w-6 h-6 text-orange-500" /></div>
            <div><div className="text-2xl font-bold text-gray-900">{counts.nouveau}</div><div className="text-sm text-gray-500">Nouveaux</div></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Calendar className="w-6 h-6 text-blue-500" /></div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {contacts.filter(c => new Date(c.timestamp).toDateString() === new Date().toDateString()).length}
              </div>
              <div className="text-sm text-gray-500">Aujourd'hui</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Liste des contacts</h2>
          <StatusFilters counts={counts} active={filter} onChange={setFilter} />
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucun contact dans cette catégorie.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    onDelete={onDelete}
                    onMarkRead={(id) => changeStatus(id, 'lu')}
                    onMarkProcessed={(id) => changeStatus(id, 'traite')}
                    onMarkNew={(id) => changeStatus(id, 'nouveau')}
                    formatDate={formatDate}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default ContactsTab;
